import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { LogOut, Mail, Download as DownloadIcon, Inbox } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("enquiries");

  useEffect(() => {
    if (user === false) navigate("/login", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    (async () => {
      try {
        const [s, d] = await Promise.all([
          axios.get(`${API}/admin/submissions`, { withCredentials: true }),
          axios.get(`${API}/admin/downloads`, { withCredentials: true }),
        ]);
        setSubmissions(s.data || []);
        setDownloads(d.data || []);
      } catch {
        /* handled by auth redirect */
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  if (!user || user.role !== "admin") {
    return (
      <div className="mx-auto max-w-6xl px-5 py-24 text-center text-melt-indigo/70" data-testid="admin-checking">
        Checking your session…
      </div>
    );
  }

  const tabBtn = (id, label, count) => (
    <button
      type="button"
      onClick={() => setTab(id)}
      aria-pressed={tab === id}
      className={[
        "rounded-full px-5 py-2 text-sm font-semibold transition-colors",
        tab === id ? "bg-melt-teal text-white" : "border border-melt-grey bg-white text-melt-indigo hover:border-melt-teal",
      ].join(" ")}
      data-testid={`admin-tab-${id}`}
    >
      {label} ({count})
    </button>
  );

  return (
    <section className="mx-auto max-w-6xl px-5 py-16 md:px-8" data-testid="admin-dashboard">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-melt-indigo">Admin dashboard</h1>
          <p className="mt-1 text-[15px] text-melt-indigo/70">Signed in as {user.email}</p>
        </div>
        <button
          type="button"
          onClick={async () => { await logout(); navigate("/login"); }}
          className="inline-flex items-center gap-2 self-start rounded-md border border-melt-grey bg-white px-5 py-2.5 text-sm font-semibold text-melt-indigo transition-colors hover:border-melt-teal hover:text-melt-teal"
          data-testid="admin-logout"
        >
          <LogOut size={16} aria-hidden="true" />
          Sign out
        </button>
      </div>

      <div className="mt-8 flex flex-wrap gap-2">
        {tabBtn("enquiries", "Enquiries", submissions.length)}
        {tabBtn("downloads", "Downloads", downloads.length)}
      </div>

      {loading ? (
        <p className="mt-10 text-melt-indigo/70">Loading…</p>
      ) : tab === "enquiries" ? (
        <div className="mt-8 space-y-4" data-testid="admin-enquiries">
          {submissions.length === 0 ? (
            <div className="flex items-center gap-3 rounded-lg border border-melt-grey bg-white p-8 text-melt-indigo/70">
              <Inbox size={22} aria-hidden="true" /> No enquiries yet.
            </div>
          ) : (
            submissions.map((s) => (
              <article key={s.id} className="rounded-lg border border-melt-grey bg-white p-6" data-testid={`admin-enquiry-${s.id}`}>
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <h2 className="font-serif text-lg font-semibold text-melt-indigo">{s.full_name}</h2>
                  <span className="text-sm text-melt-indigo/60">{formatDate(s.created_at)}</span>
                </div>
                <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-[15px] text-melt-indigo/75">
                  <a href={`mailto:${s.email}`} className="inline-flex items-center gap-1.5 text-melt-teal hover:underline">
                    <Mail size={15} aria-hidden="true" /> {s.email}
                  </a>
                  {s.institution && <span>{s.institution}</span>}
                  {s.country && <span>{s.country}</span>}
                  <span className="rounded-full bg-melt-amber/15 px-3 py-0.5 text-[13px] font-medium text-melt-indigo">{s.reason}</span>
                </div>
                <p className="mt-3 whitespace-pre-wrap text-base text-melt-indigo/85">{s.message}</p>
              </article>
            ))
          )}
        </div>
      ) : (
        <div className="mt-8 overflow-hidden rounded-lg border border-melt-grey bg-white" data-testid="admin-downloads">
          {downloads.length === 0 ? (
            <div className="flex items-center gap-3 p-8 text-melt-indigo/70">
              <DownloadIcon size={22} aria-hidden="true" /> No downloads recorded yet.
            </div>
          ) : (
            <table className="w-full text-left">
              <thead className="border-b border-melt-grey bg-melt-off-white">
                <tr>
                  <th className="px-6 py-3 text-sm font-semibold text-melt-indigo">Document</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-melt-indigo">Word</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-melt-indigo">PDF</th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-melt-indigo">Total</th>
                </tr>
              </thead>
              <tbody>
                {downloads.map((d) => (
                  <tr key={d.slug} className="border-b border-melt-grey last:border-0">
                    <td className="px-6 py-3 text-melt-indigo">{d.title || d.slug}</td>
                    <td className="px-4 py-3 text-right text-melt-indigo/70">{d.formats?.word || 0}</td>
                    <td className="px-4 py-3 text-right text-melt-indigo/70">{d.formats?.pdf || 0}</td>
                    <td className="px-6 py-3 text-right font-semibold text-melt-indigo">{d.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </section>
  );
}
